import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import {
  useRegistrationRouteState,
  useValueIntroForm,
} from '../features/registration/model';
import {
  ValueIntroBackButton,
  ValueIntroContinueButton,
  ValueIntroField,
  ValueIntroHeading,
  ValueIntroHero,
  ValueIntroSkipButton,
} from '../features/registration/ui';
import {
  getEmployeeGoal,
  getEmployeeProfile,
} from '../shared/api/employee.service';
import { useEmployeeSession } from '../shared/hooks/use-employee-session';
import { resolveEmployeeSessionCountryCode } from '../shared/model';

const FOOTER_GAP_PX = 14;
const FOOTER_BOTTOM_PADDING_PX = 8;
const FOOTER_SECONDARY_MIN_HEIGHT_PX = 36;
const FOOTER_SECONDARY_ERROR_MIN_HEIGHT_PX = 52;

const Screen = styled.div.attrs({
  'data-ui': 'registration-goals-value-page',
})`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  & > [data-ui='registration-goals-value-intro-header'] {
    margin-bottom: ${({ theme }) => theme.scale.px(15)};
  }

  & > [data-ui='registration-goals-value-intro-hero'] {
    align-self: center;
    margin-bottom: ${({ theme }) => theme.scale.px(12)};
  }

  & > [data-ui='registration-goals-value-intro-field'] {
    align-self: center;
    margin-bottom: auto;
  }
`;

const IntroHeader = styled.div.attrs({
  'data-ui': 'registration-goals-value-intro-header',
})`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(25)};

  & > [data-ui='registration-goals-value-intro-back-button'] {
    margin-left: ${({ theme }) => theme.scale.px(35)};
  }
`;

const SubmitError = styled.p`
  margin: 0;
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.support.danger};
`;

const Footer = styled.div.attrs({
  'data-ui': 'registration-goals-value-page-footer',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  row-gap: ${({ theme }) => theme.scale.px(FOOTER_GAP_PX)};
  padding-bottom: ${({ theme }) => theme.scale.px(FOOTER_BOTTOM_PADDING_PX)};
`;

const FooterSecondary = styled.div.attrs({
  'data-ui': 'registration-goals-value-page-footer-secondary',
})<{ $minHeightPx: number }>`
  width: 100%;
  min-height: ${({ $minHeightPx, theme }) => theme.scale.px($minHeightPx)};
  display: grid;
  justify-items: center;
  align-content: start;
`;

function resolveValueFormKey(
  countryCode: string,
  goalText?: string,
  targetAmount?: number,
): string {
  return JSON.stringify([countryCode, goalText ?? null, targetAmount ?? null]);
}

type ValuePageFormProps = {
  countryCode: string;
  initialGoalText?: string;
  initialGoalValueAmount?: number | null;
};

function ValuePageForm({
  countryCode,
  initialGoalText,
  initialGoalValueAmount,
}: ValuePageFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const valueIntroForm = useValueIntroForm({
    countryCode,
    initialGoalText,
    initialGoalValueAmount,
  });
  const registrationRoute = useRegistrationRouteState();
  const previousPath = registrationRoute.previousImplementedPath;
  const nextPath = registrationRoute.nextImplementedPath;

  return (
    <Screen>
      <IntroHeader>
        <ValueIntroBackButton
          ariaLabel={t('registrationGoalValue.backAria')}
          onPress={() => previousPath && navigate(previousPath)}
        />
        <ValueIntroHeading />
      </IntroHeader>

      <ValueIntroHero />

      <ValueIntroField
        currencyPrefix={valueIntroForm.currencyPrefix}
        goalValueInput={valueIntroForm.goalValueInput}
        thousandsSeparator={valueIntroForm.thousandsSeparator}
        onGoalValueChange={valueIntroForm.setGoalValue}
      />
      <Footer>
        <ValueIntroContinueButton
          canSubmit={valueIntroForm.canSubmit && !valueIntroForm.isSubmitting}
          onContinue={async () => {
            if (
              !valueIntroForm.canSubmit ||
              valueIntroForm.isSubmitting ||
              !nextPath
            ) {
              return;
            }

            const isSubmitted = await valueIntroForm.submit();
            if (!isSubmitted) {
              return;
            }

            navigate(nextPath);
          }}
        />
        <FooterSecondary
          $minHeightPx={
            valueIntroForm.submitError
              ? FOOTER_SECONDARY_ERROR_MIN_HEIGHT_PX
              : FOOTER_SECONDARY_MIN_HEIGHT_PX
          }
        >
          <ValueIntroSkipButton
            isDisabled={valueIntroForm.isSubmitting}
            onSkip={async () => {
              if (valueIntroForm.isSubmitting || !nextPath) {
                return;
              }

              const isSkipped = await valueIntroForm.skip();
              if (!isSkipped) {
                return;
              }

              navigate(nextPath);
            }}
          />
          {valueIntroForm.submitError ? (
            <SubmitError>{valueIntroForm.submitError}</SubmitError>
          ) : null}
        </FooterSecondary>
      </Footer>
    </Screen>
  );
}

export function ValuePage() {
  const { authContact, status } = useEmployeeSession();
  const goalQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeGoal,
    queryKey: ['employee', 'goal'],
  });
  const profileQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeProfile,
    queryKey: ['employee', 'profile'],
  });
  const goal = goalQuery.data ?? null;
  const profile = profileQuery.data ?? null;
  const countryCode = resolveEmployeeSessionCountryCode(profile, authContact);

  return (
    <ValuePageForm
      key={resolveValueFormKey(countryCode, goal?.text, goal?.targetAmount)}
      countryCode={countryCode}
      initialGoalText={goal?.text}
      initialGoalValueAmount={goal?.targetAmount ?? null}
    />
  );
}
